import { useMemo, useState } from 'react';
import { Link } from 'react-router';
import { api } from '../../api/client';
import { Card, Chip, Empty, LoadError, Loading, PageHeader } from '../../components/ui';
import { Icon } from '../../lib/icons';
import { initials } from '../../lib/session';
import { useData } from '../../lib/useData';

export function AdminTenants() {
  const tenants = useData(() => api.tenants(), []);
  const [q, setQ] = useState('');
  const list = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const all = tenants.data?.tenants ?? [];
    return needle ? all.filter((t) => t.name.toLowerCase().includes(needle) || t.domain.includes(needle)) : all;
  }, [tenants.data, q]);

  return (
    <>
      <PageHeader
        eyebrow="Platform"
        title="Tenants"
        chips={tenants.data && <Chip>{tenants.data.tenants.length}</Chip>}
        actions={
          <label className="search">
            <Icon name="search" size={17} strokeWidth={2} />
            <span className="sr-only">Search tenants</span>
            <input type="search" placeholder="Search by name or domain" value={q} onChange={(e) => setQ(e.target.value)} />
          </label>
        }
      />
      <div className="content">
        <Card>
          {tenants.error ? (
            <LoadError error={tenants.error} retry={tenants.reload} />
          ) : !tenants.data ? (
            <Loading label="Loading tenants" />
          ) : list.length === 0 ? (
            <Empty title={q ? 'No tenants match' : 'No tenants'} />
          ) : (
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th scope="col">Tenant</th>
                    <th scope="col" className="right">
                      People
                    </th>
                    <th scope="col">Customer</th>
                    <th scope="col">Changes</th>
                    <th scope="col">
                      <span className="sr-only">Actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {list.map((t) => (
                    <tr key={t.domain}>
                      <td>
                        <span style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                          <span className="avatar" aria-hidden="true">
                            {initials(t.name)}
                          </span>
                          <span className="cell-stack">
                            <strong style={{ fontWeight: 650 }}>{t.name}</strong>
                            <small className="mono">{t.domain}</small>
                          </span>
                        </span>
                      </td>
                      <td className="right">{t.extensionCount ?? '–'}</td>
                      <td>
                        {t.customer ? (
                          <Link to={`/admin/customers/${t.customer.id}`}>{t.customer.status === 'onboarding' ? `${t.customer.name} (setting up)` : t.customer.name}</Link>
                        ) : (
                          <Link to="/admin/customers" className="muted">
                            Not in Stitch yet
                          </Link>
                        )}
                      </td>
                      <td>{t.writable ? <Chip tone="ok">Allowed</Chip> : <Chip>Read-only</Chip>}</td>
                      <td>
                        <div className="row-actions">
                          <Link className="btn secondary small" to={`/t/${encodeURIComponent(t.domain)}`}>
                            Open portal
                            <Icon name="chevRight" size={15} strokeWidth={2.2} />
                          </Link>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
        <p className="muted" style={{ margin: 0 }}>
          Which tenants can change is set by PBX_WRITE_TENANTS on the server (* for every tenant). Opening a portal shows it as the customer’s admin sees it.
        </p>
      </div>
    </>
  );
}
